import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const categoryOptions = [
  "Press Coverage",
  "Endorsement",
  "Interview",
  "Opinion/Editorial",
  "Radio/Podcast",
  "TV Appearance",
];

const defaultFormValues = {
  title: "",
  source: "",
  publicationDate: "",
  url: "",
  category: "Press Coverage",
  author: "",
  excerpt: "",
  imageUrl: "",
  isFeatured: false,
};

const CreateEditArticalModal = ({
  open,
  handleClose,
  articleData,
  onSave,
  isSaving = false,
}) => {
  const isEdit = Boolean(articleData);
  const [formValues, setFormValues] = useState(defaultFormValues);

  useEffect(() => {
    if (open) {
      setFormValues(articleData || defaultFormValues);
    }
  }, [articleData, open]);

  const excerptCount = useMemo(
    () => formValues.excerpt.length,
    [formValues.excerpt],
  );

  const handleChange = (field) => (event) => {
    const value =
      field === "isFeatured" ? event.target.checked : event.target.value;

    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    onSave(formValues);
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          bgcolor: "#081B44",
          color: "#fff",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "18px" }}>
          {isEdit ? "Edit Article" : "New Article"}
        </Typography>

        <IconButton
          onClick={handleClose}
          disabled={isSaving}
          sx={{
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.35)",
            width: 30,
            height: 30,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography sx={labelStyle}>ARTICLE TITLE *</Typography>
            <TextField
              fullWidth
              size="small"
              value={formValues.title}
              onChange={handleChange("title")}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: "1 1 320px" }}>
              <Typography sx={labelStyle}>SOURCE *</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Delaware State News"
                value={formValues.source}
                onChange={handleChange("source")}
              />
            </Box>

            <Box sx={{ flex: "1 1 220px" }}>
              <Typography sx={labelStyle}>PUBLICATION DATE *</Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={formValues.publicationDate}
                onChange={handleChange("publicationDate")}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>

          <Box>
            <Typography sx={labelStyle}>ARTICLE URL *</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="https://..."
              value={formValues.url}
              onChange={handleChange("url")}
            />
            <Typography sx={helperStyle}>
              Where the article is published — this link is what visitors click
              on from the site.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: "1 1 220px" }}>
              <Typography sx={labelStyle}>CATEGORY *</Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={formValues.category}
                onChange={handleChange("category")}
              >
                {categoryOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ flex: "1 1 220px" }}>
              <Typography sx={labelStyle}>AUTHOR</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Optional"
                value={formValues.author}
                onChange={handleChange("author")}
              />
            </Box>
          </Box>

          <Box>
            <Typography sx={labelStyle}>EXCERPT *</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="A short preview of the article (max 300 characters)"
              value={formValues.excerpt}
              onChange={handleChange("excerpt")}
              slotProps={{ htmlInput: { maxLength: 300 } }}
            />
            <Typography sx={helperStyle}>{excerptCount} / 300 characters</Typography>
          </Box>

          <Box>
            <Typography sx={labelStyle}>IMAGE URL</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Optional — https://..."
              value={formValues.imageUrl}
              onChange={handleChange("imageUrl")}
            />
          </Box>

          <Box
            sx={{
              bgcolor: "#F7F9FC",
              borderRadius: "4px",
              px: 1,
              py: 0.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={formValues.isFeatured}
                  onChange={handleChange("isFeatured")}
                  sx={{
                    color: "#B0B7C3",
                    "&.Mui-checked": {
                      color: "#D4A017",
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: "13px", color: "#25324B" }}>
                  Featured article (highlighted on the site)
                </Typography>
              }
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mt: 3,
            pt: 2,
            borderTop: "1px solid #E5E7EB",
          }}
        >
          <Button
            onClick={handleClose}
            disabled={isSaving}
            sx={{
              bgcolor: "#E5E7EB",
              color: "#0F1E3D",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            sx={{
              bgcolor: "#D4A017",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              "&:hover": {
                bgcolor: "#B8860B",
              },
            }}
          >
            {isSaving
              ? "Saving..."
              : isEdit
                ? "Save Changes"
                : "Save Article"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const labelStyle = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#25324B",
  letterSpacing: "0.4px",
  mb: 0.75,
};

const helperStyle = {
  fontSize: "11px",
  color: "#98A2B3",
  mt: 0.75,
};

export default CreateEditArticalModal;
